// API service for Dataverse data access
// Public routes go through Azure Function proxy (limited fields)
// Authenticated routes go direct to Dataverse Web API (full access)

const DATAVERSE_URL = "https://orgde5cf563.crm9.dynamics.com/api/data/v9.2";
// TODO: Set this to your Azure Function URL when deployed
//const PUBLIC_API_URL = process.env.REACT_APP_PUBLIC_API_URL || "/api";

// Municipality label map
export const MUNICIPIO_MAP: Record<number, string> = {
  154890000:"Adjuntas",154890001:"Aguada",154890002:"Aguadilla",
  154890003:"Aguas Buenas",154890004:"Aibonito",154890005:"Arecibo",
  154890006:"Arroyo",154890007:"Añasco",154890008:"Barceloneta",
  154890009:"Barranquitas",154890010:"Bayamón",154890011:"Cabo Rojo",
  154890012:"Caguas",154890013:"Camuy",154890014:"Canóvanas",
  154890015:"Carolina",154890016:"Cataño",154890017:"Cayey",
  154890018:"Ceiba",154890019:"Ciales",154890020:"Cidra",
  154890021:"Coamo",154890022:"Comerío",154890023:"Corozal",
  154890024:"Culebra",154890025:"Dorado",154890026:"Fajardo",
  154890027:"Florida",154890028:"Guánica",154890029:"Guayama",
  154890030:"Guayanilla",154890031:"Guaynabo",154890032:"Gurabo",
  154890033:"Hatillo",154890034:"Hormigueros",154890035:"Humacao",
  154890036:"Isabela",154890037:"Jayuya",154890038:"Juana Díaz",
  154890039:"Juncos",154890040:"Lajas",154890041:"Lares",
  154890042:"Las Marías",154890043:"Las Piedras",154890044:"Loíza",
  154890045:"Luquillo",154890046:"Manatí",154890047:"Maricao",
  154890048:"Maunabo",154890049:"Mayagüez",154890050:"Moca",
  154890051:"Morovis",154890052:"Naguabo",154890053:"Naranjito",
  154890054:"Orocovis",154890055:"Patillas",154890056:"Peñuelas",
  154890057:"Ponce",154890058:"Quebradillas",154890059:"Rincón",
  154890060:"Río Grande",154890061:"Sabana Grande",154890062:"Salinas",
  154890063:"San Germán",154890064:"San Juan",154890065:"San Lorenzo",
  154890066:"San Sebastián",154890067:"Santa Isabel",154890068:"Toa Alta",
  154890069:"Toa Baja",154890070:"Trujillo Alto",154890071:"Utuado",
  154890072:"Vega Alta",154890073:"Vega Baja",154890074:"Vieques",
  154890075:"Villalba",154890076:"Yabucoa",154890077:"Yauco",
};

export const MUNICIPIO_REVERSE: Record<string, number> = Object.fromEntries(
  Object.entries(MUNICIPIO_MAP).map(([k, v]) => [v, Number(k)])
);

// ── Types ──────────────────────────────────────────────────────
export interface Parcel {
  id: string;
  parcelId: string;
  municipio: string;
  availabilityStatus: string;
  cabida: number;
  fincaId: string;
  description: string;
  xCentroid: number;
  yCentroid: number;
  nombreAdquisicion: string;
  uso: string;
}

export interface Contract {
  id: string;
  contractId: string;
  clientName: string;
  municipio: string;
  fechaContrato: string;
  fechaVencimiento: string;
  cuantia: number;
  activo: boolean;
  categoria: string;
  status: string;
}

export interface SubParcel {
  id: string;
  subParcelId: string;
  municipio: string;
  cabida: number;
  estatus: string;
  uso: string;
}

export interface Lease {
  id: string;
  leaseId: string;
  clientName: string;
  numeroCliente: string;
  parcel: string;
  contract: string;
  startDate: string | null;
  endDate: string | null;
  rentAmount: number;
  valorEnLibro: number;
  status: string;
  leaseTerm: string;
  purposeOfUse: string;
  anoFiscal: string;
  comentario: string;
}

// ── Availability status map ────────────────────────────────────
const AVAILABILITY_MAP: Record<number, string> = {
  154890000: "Disponible",
  154890001: "Arrendada",
  154890002: "Vendida",
  154890003: "En Proceso",
};

// ── Public API (no auth required) ──────────────────────────────
export async function searchParcelsPublic(
  query: string,
  municipio?: string,
  token?: string
): Promise<Parcel[]> {
  const headers: Record<string, string> = {
    ...(token ? { "Authorization": `Bearer ${token}` } : {}),
    "OData-MaxVersion": "4.0",
    "OData-Version": "4.0",
    "Accept": "application/json",
    "Prefer": "odata.include-annotations=*",
  };
  const select = "cr49f_atparcelid,cr49f_parcelid,cr49f_listamunicipios,cr49f_availabilitystatus,cr49f_parcelsizesqft,cr49f_numerodefincaregistral,cr49f_description,cr750_x_centroid,cr750_y_centroid";

  // Server-side filter: only active records, optionally scoped to one municipio
  let parcelFilter = "statecode eq 0";
  if (municipio && municipio !== "all") {
    const muniCode = MUNICIPIO_REVERSE[municipio];
    if (muniCode !== undefined) {
      parcelFilter += ` and cr49f_listamunicipios eq ${muniCode}`;
    }
  }

  const parcelUrl = `${DATAVERSE_URL}/cr49f_atparcels?$select=${select}&$filter=${encodeURIComponent(parcelFilter)}&$top=200&$orderby=${encodeURIComponent("cr49f_parcelid asc")}`;
  const parcelResp = await fetch(parcelUrl, { headers });
  if (!parcelResp.ok) {
    const err = await parcelResp.json().catch(() => ({}));
    console.error("[Dataverse] searchParcelsPublic failed", parcelResp.status, err?.error?.message ?? err);
    throw new Error(err?.error?.message ?? `Dataverse ${parcelResp.status}`);
  }
  const parcelData = await parcelResp.json();
  const allParcels = (parcelData.value || []).map(mapParcel);

  // Client-side wildcard: matches any part of parcelId, fincaId, municipio, or description
  if (!query.trim()) return allParcels;
  const q = query.trim().toLowerCase();
  return allParcels.filter((p: Parcel) =>
    p.parcelId.toLowerCase().includes(q) ||
    p.fincaId.toLowerCase().includes(q) ||
    p.municipio.toLowerCase().includes(q) ||
    p.description.toLowerCase().includes(q)
  );
}

export async function getParcelById(parcelId: string, token: string): Promise<Parcel | null> {
  const filter = `cr49f_parcelid eq '${parcelId}' and statecode eq 0`;
  const select = "cr49f_atparcelid,cr49f_parcelid,cr49f_listamunicipios,cr49f_availabilitystatus,cr49f_parcelsizesqft,cr49f_numerodefincaregistral,cr49f_description,cr750_x_centroid,cr750_y_centroid";
  const url = `${DATAVERSE_URL}/cr49f_atparcels?$select=${select}&$filter=${encodeURIComponent(filter)}&$top=1`;

  const resp = await fetch(url, {
    headers: { "Authorization": `Bearer ${token}`, "Accept": "application/json", "OData-Version": "4.0" },
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    console.error("[Dataverse] getParcelById failed", resp.status, err?.error?.message ?? err);
    throw new Error(err?.error?.message ?? `Dataverse ${resp.status}`);
  }
  const data = await resp.json();
  if (data.value && data.value.length > 0) {
    return mapParcel(data.value[0]);
  }
  return null;
}

// ── Authenticated API (Entra ID token required) ────────────────
export async function searchContracts(
  token: string,
  query: string,
  municipio?: string,
  options?: { expiringDays?: number; activo?: boolean }
): Promise<Contract[]> {
  const filters: string[] = ["statecode eq 0"];

  if (municipio && municipio !== "all") {
    const muniCode = MUNICIPIO_REVERSE[municipio];
    if (muniCode !== undefined) filters.push(`cr49f_municipio eq ${muniCode}`);
  }
  if (options?.activo !== undefined) {
    filters.push(`cr49f_activo eq ${options.activo}`);
  }
  if (options?.expiringDays !== undefined) {
    const today = new Date().toISOString();
    const future = new Date(Date.now() + options.expiringDays * 86400000).toISOString();
    filters.push(`cr49f_fechavencimiento ge ${today} and cr49f_fechavencimiento le ${future}`);
  }

  const select = "cr49f_atcontractid,cr49f_contractid,_cr49f_namecliente_value,cr49f_municipio,cr49f_fechacontrato,cr49f_fechavencimiento,cr49f_cuantia,cr49f_activo,cr49f_categoria";
  const url = `${DATAVERSE_URL}/cr49f_atcontracts?$select=${select}&$filter=${encodeURIComponent(filters.join(" and "))}&$top=200&$orderby=cr49f_contractid asc`;

  const resp = await fetch(url, {
    headers: {
      "Authorization": `Bearer ${token}`,
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Accept": "application/json",
      "Prefer": "odata.include-annotations=*",
    },
  });
  const data = await resp.json();

  // Client-side wildcard filter: matches contract ID or client name
  const q = query.trim().toLowerCase();
  const rows: Record<string, unknown>[] = (data.value || []).filter((e: Record<string, unknown>) => {
    if (!q) return true;
    const contractId = ((e.cr49f_contractid as string) ?? "").toLowerCase();
    const clientName = ((e["_cr49f_namecliente_value@OData.Community.Display.V1.FormattedValue"] as string) ?? "").toLowerCase();
    return contractId.includes(q) || clientName.includes(q);
  });

  return rows.map((e) => mapContract(e));
}

export async function getContractById(
  token: string,
  contractId: string
): Promise<Contract | null> {
  const filter = `cr49f_contractid eq '${contractId}' and statecode eq 0`;
  const select = "cr49f_atcontractid,cr49f_contractid,_cr49f_namecliente_value,cr49f_municipio,cr49f_fechacontrato,cr49f_fechavencimiento,cr49f_cuantia,cr49f_activo,cr49f_categoria";
  const url = `${DATAVERSE_URL}/cr49f_atcontracts?$select=${select}&$filter=${filter}&$top=1`;

  const resp = await fetch(url, {
    headers: {
      "Authorization": `Bearer ${token}`,
      "Accept": "application/json",
      "OData-Version": "4.0",
      "Prefer": "odata.include-annotations=*",
    },
  });
  const data = await resp.json();
  if (data.value && data.value.length > 0) {
    // Get client name
    const e = data.value[0];
    const clientMap: Record<string, string> = {};
    if (e._cr49f_namecliente_value) {
      try {
        const cr = await fetch(`${DATAVERSE_URL}/cr49f_atclients(${e._cr49f_namecliente_value})?$select=cr49f_clientid`, {
          headers: { "Authorization": `Bearer ${token}`, "Accept": "application/json" },
        });
        const cd = await cr.json();
        clientMap[e._cr49f_namecliente_value] = cd.cr49f_clientid ?? "Sin nombre";
      } catch {
        clientMap[e._cr49f_namecliente_value] = "—";
      }
    }
    return mapContract(e, clientMap);
  }
  return null;
}

function mapLease(e: Record<string, unknown>): Lease {
  return {
    id: (e.cr49f_atleasesid as string) ?? "",
    leaseId: (e.cr49f_leaseid as string) ?? "—",
    clientName: (e["_cr49f_clientid_value@OData.Community.Display.V1.FormattedValue"] as string) ?? "—",
    numeroCliente: (e.cr49f_numerocliente as string) ?? "—",
    parcel: (e["_cr49f_parcel_value@OData.Community.Display.V1.FormattedValue"] as string) ?? "—",
    contract: (e["_cr49f_atcontract_value@OData.Community.Display.V1.FormattedValue"] as string) ?? "—",
    startDate: (e.cr49f_startdate as string) ?? null,
    endDate: (e.cr49f_enddate as string) ?? null,
    rentAmount: (e.cr49f_rentamount as number) ?? 0,
    valorEnLibro: (e.cr49f_valorenlibro as number) ?? 0,
    status: (e["cr49f_leasestatus@OData.Community.Display.V1.FormattedValue"] as string) ?? "—",
    leaseTerm: (e.cr49f_leaseterm as string) ?? "—",
    purposeOfUse: (e.cr49f_purposeofuse as string) ?? "—",
    anoFiscal: (e.cr49f_anofiscal as string) ?? "—",
    comentario: (e.cr49f_comentario as string) ?? "",
  };
}

// ── Sub-parcels ────────────────────────────────────────────────
export async function getSubParcelsByParcelId(parcelGuid: string, token: string): Promise<SubParcel[]> {
  const filter = `_cr49f_atparcel_value eq ${parcelGuid} and statecode eq 0`;
  const select = "cr49f_atsubparcelid,cr49f_subparcelid,cr49f_municipio,cr49f_cabida,cr49f_estatus,cr49f_uso";
  const url = `${DATAVERSE_URL}/cr49f_atsubparcels?$select=${select}&$filter=${encodeURIComponent(filter)}&$top=50`;

  const resp = await fetch(url, {
    headers: {
      "Authorization": `Bearer ${token}`,
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Accept": "application/json",
      "Prefer": "odata.include-annotations=*",
    },
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    console.error("[Dataverse] getSubParcelsByParcelId failed", resp.status, err?.error?.message ?? err);
    return [];
  }
  const data = await resp.json();
  return (data.value || []).map(mapSubParcel);
}

// ── Mappers ────────────────────────────────────────────────────
function mapParcel(e: Record<string, unknown>): Parcel {
  return {
    id: (e.cr49f_atparcelid as string) ?? "",
    parcelId: (e.cr49f_parcelid as string) ?? "",
    municipio: (e["cr49f_listamunicipios@OData.Community.Display.V1.FormattedValue"] as string)
      ?? MUNICIPIO_MAP[(e.cr49f_listamunicipios as number) ?? 0] ?? "—",
    availabilityStatus: (e["cr49f_availabilitystatus@OData.Community.Display.V1.FormattedValue"] as string)
      ?? AVAILABILITY_MAP[(e.cr49f_availabilitystatus as number) ?? 0] ?? "—",
    cabida: (e.cr49f_parcelsizesqft as number) ?? 0,
    fincaId: (e.cr49f_numerodefincaregistral as string) ?? "",
    description: (e.cr49f_description as string) ?? "",
    xCentroid: (e["cr750_x_centroid"] as number) ?? 0,
    yCentroid: (e["cr750_y_centroid"] as number) ?? 0,
    nombreAdquisicion: "",
    uso: "",
  };
}

function mapSubParcel(e: Record<string, unknown>): SubParcel {
  return {
    id: (e.cr49f_atsubparcelid as string) ?? "",
    subParcelId: (e.cr49f_subparcelid as string) ?? "",
    municipio: (e["cr49f_municipio@OData.Community.Display.V1.FormattedValue"] as string)
      ?? MUNICIPIO_MAP[(e.cr49f_municipio as number) ?? 0] ?? "—",
    cabida: (e.cr49f_cabida as number) ?? 0,
    estatus: (e["cr49f_estatus@OData.Community.Display.V1.FormattedValue"] as string)
      ?? (e.cr49f_estatus as string) ?? "—",
    uso: (e.cr49f_uso as string) ?? "",
  };
}

function mapContract(e: Record<string, unknown>, clientMap: Record<string, string> = {}): Contract {
  return {
    id: (e.cr49f_atcontractid as string) ?? "",
    contractId: (e.cr49f_contractid as string) ?? "",
    clientName: (e["_cr49f_namecliente_value@OData.Community.Display.V1.FormattedValue"] as string)
      ?? clientMap[(e._cr49f_namecliente_value as string) ?? ""]
      ?? "Sin cliente",
    municipio: MUNICIPIO_MAP[(e.cr49f_municipio as number) ?? 0] ?? "—",
    fechaContrato: (e.cr49f_fechacontrato as string) ?? "",
    fechaVencimiento: (e.cr49f_fechavencimiento as string) ?? "",
    cuantia: (e.cr49f_cuantia as number) ?? 0,
    activo: (e.cr49f_activo as boolean) ?? false,
    categoria: (e["cr49f_categoria@OData.Community.Display.V1.FormattedValue"] as string) ?? "—",
    status: (e.cr49f_activo as boolean) ? "Activo" : "Inactivo",
  };
}

export async function fetchInspectionsDirect(
  token: string,
  municipality?: string
): Promise<import("./inspectionApi").Inspection[]> {
  const filters: string[] = [];
  if (municipality) filters.push(`contains(cr49f_municipality,'${municipality}')`);
  const filterStr = filters.length > 0 ? `&$filter=${filters.join(" and ")}` : "";

  const select = [
    "cr49f_atinspectionid","cr49f_inspectionid_name","cr49f_municipality",
    "cr49f_scheduledate","cr49f_completeddate","_cr49f_inspectorid_value",
    "cr49f_inspectionstatus","cr49f_overallresult","_cr49f_subparcelid_value",
    "cr49f_followuprequired",
  ].join(",");

  const url = `${DATAVERSE_URL}/cr49f_atinspections?$select=${select}${filterStr}&$orderby=cr49f_scheduledate desc&$top=200`;

  const resp = await fetch(url, {
    headers: {
      "Authorization": `Bearer ${token}`,
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Accept": "application/json",
      "Prefer": "odata.include-annotations=*",
    },
  });
  if (!resp.ok) throw new Error(`Dataverse ${resp.status}`);
  const data = await resp.json();
  const { mapRow } = await import("./inspectionApi");
  return (data.value ?? []).map(mapRow);
}

export async function fetchLeasesDirect(
  token: string,
  search?: string
): Promise<Lease[]> {
  const select = [
    "cr49f_atleasesid","cr49f_leaseid","cr49f_numerocliente",
    "_cr49f_clientid_value","_cr49f_parcel_value","_cr49f_atcontract_value",
    "cr49f_startdate","cr49f_enddate","cr49f_rentamount","cr49f_valorenlibro",
    "cr49f_leasestatus","cr49f_leaseterm","cr49f_purposeofuse",
    "cr49f_anofiscal","cr49f_comentario",
  ].join(",");

  const url = `${DATAVERSE_URL}/cr49f_atleaseses?$select=${select}&$orderby=cr49f_startdate desc&$top=200`;

  const resp = await fetch(url, {
    headers: {
      "Authorization": `Bearer ${token}`,
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Accept": "application/json",
      "Prefer": "odata.include-annotations=*",
    },
  });
  if (!resp.ok) throw new Error(`Dataverse ${resp.status}`);
  const data = await resp.json();
  const allLeases = (data.value ?? []).map(mapLease);

  // Client-side wildcard: matches leaseId, client name, número, sub-parcel, or contract
  if (!search?.trim()) return allLeases;
  const q = search.trim().toLowerCase();
  return allLeases.filter((l: Lease) =>
    l.leaseId.toLowerCase().includes(q) ||
    l.clientName.toLowerCase().includes(q) ||
    l.numeroCliente.toLowerCase().includes(q) ||
    l.parcel.toLowerCase().includes(q) ||
    l.contract.toLowerCase().includes(q)
  );
}
